/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import { useState, useMemo, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Form, TabContainer, Nav, Tab } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { showAlert } from 'store/alert/alert.thunk';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import AccordionAdvancedTable from 'components/base/AccordionAdvancedTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import Button from 'components/base/Button';
import menuService from 'service/menu';
import userService from 'service/user';
import { authActions } from 'store/auth/auth.slice';

interface SimplifiedRoleType {
  name: string;
  pid?: number;
  children?: SimplifiedRoleType[];
}

interface SimplifiedPosRoleType {
  name: string;
  pid?: number;
}

const transformData = (data: any[], i18n: any): SimplifiedRoleType[] => {
  const categoryMap = new Map<string, SimplifiedRoleType>();

  data?.forEach(item => {
    categoryMap.set(item?.pid, {
      name: item?.description?.[i18n?.language] || '',
      pid: item?.pid,
      children: []
    });
  });

  data?.forEach(item => {
    if (item.parent_pid) {
      const parent = categoryMap.get(item.parent_pid);
      if (parent) {
        parent?.children?.push(categoryMap.get(item?.pid)!);
      }
    }
  });

  const topLevelCategories: SimplifiedRoleType[] = [];
  categoryMap.forEach((category, pid) => {
    if (!data.find(item => item?.pid === pid)?.parent_pid) {
      topLevelCategories.push(category);
    }
  });

  return topLevelCategories;
};

const LoadingSpinner = () => (
  <div className="loading-spinner">
    <div className="spinner"></div>
  </div>
);

const ClientsRoles = () => {
  const { t, i18n } = useTranslation();

  const { state } = useLocation();
  const { full_name, user_id_number } = state;
  const dispatch: Dispatch<any> = useDispatch();
  const userIdNumber = useSelector(
    (state: { auth: { user: { id_number: string } } }) =>
      state?.auth?.user?.id_number
  );

  const [key, setKey] = useState('WEB');
  const [selectedPermissions, setSelectedPermissions] = useState<
    { menu_pid: number }[]
  >([]);

  const { data, isLoading, isFetching, refetch } = useQuery(
    ['GET_MENU_LIST', key],
    () => {
      const res = menuService
        .getList({ application_type: key })
        .then((res: any) => res);
      return res;
    },
    { enabled: !!key }
  );

  const { refetch: refetchPermissions } = useQuery(
    ['GET_MENU_LIST', key, user_id_number, userIdNumber],
    () => {
      if (user_id_number && userIdNumber) {
        const res = userService
          .getUserPermision({ application_type: key, user_id_number })
          .then((res: any) => {
            if (res) {
              setSelectedPermissions(res?.data?.user_permissions || []);

              if (+userIdNumber === +user_id_number && key === 'WEB') {
                dispatch(
                  authActions.updateUserPermissions(res?.data?.user_permissions)
                );
              }

              return res;
            }
          });
        return res;
      }
    },
    {
      enabled: !!key && !!user_id_number && !!userIdNumber
    }
  );

  const CreatedData: SimplifiedRoleType[] = useMemo(() => {
    return data ? transformData(data?.data?.menus, i18n) : [];
  }, [data, i18n?.language]);

  const CreateDataPos: SimplifiedRoleType[] = useMemo(() => {
    return data ? transformData(data?.data?.menus, i18n) : [];
  }, [data, i18n?.language]);

  const allPids = useMemo(() => {
    const pids: number[] = [];
    const extractPids = (items: SimplifiedRoleType[]) => {
      items.forEach(item => {
        if (item.pid) pids.push(item.pid);
        if (item.children) extractPids(item.children);
      });
    };
    extractPids(key === 'WEB' ? CreatedData : CreateDataPos);
    return pids;
  }, [CreatedData, CreateDataPos, key]);

  const selectAllChecked = useMemo(() => {
    const isAllSelected =
      allPids.length > 0 &&
      allPids.every(pid =>
        selectedPermissions.some(perm => perm.menu_pid === pid)
      );
    return isAllSelected;
  }, [allPids, selectedPermissions]);

  const handleTogglePermission = useCallback((pid: number) => {
    setSelectedPermissions(prev => {
      const isSelected = prev.some(perm => perm.menu_pid === pid);
      const newPermissions = isSelected
        ? prev.filter(perm => perm.menu_pid !== pid) // Remove pid if unchecked
        : [...prev, { menu_pid: pid }]; // Add pid if checked
      return newPermissions;
    });
  }, []);

  // Handle select all checkbox
  const handleSelectAll = useCallback(() => {
    if (selectAllChecked) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions(allPids.map(pid => ({ menu_pid: pid })));
    }
  }, [selectAllChecked, allPids]);

  const userTableColumns: ColumnDef<SimplifiedRoleType>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: t('role_name'),
        cell: (rowData: any) => {
          return (
            <div style={{ paddingLeft: `${rowData?.row?.depth * 20}px` }}>
              {rowData?.row?.children?.length > 0 ? (
                <span style={{ fontWeight: 'bold' }}>{rowData?.row?.name}</span>
              ) : (
                <span>{rowData?.row?.name}</span>
              )}
            </div>
          );
        },
        meta: { cellProps: { className: 'text-900' } }
      },
      {
        accessorKey: 'check',
        header: () => (
          <div className="d-flex gap-2 align-items-center">
            <Form.Check
              type="checkbox"
              checked={selectAllChecked}
              onChange={handleSelectAll}
            />
            <p className="mb-0">{t('status')}</p>
          </div>
        ),
        cell: (rowData: any) => {
          const pid = rowData.row.pid;
          if (!pid) return null;
          const isChecked = selectedPermissions.some(
            perm => perm.menu_pid === pid
          );

          return (
            <div>
              <Form.Check
                type="checkbox"
                checked={isChecked}
                onChange={() => handleTogglePermission(pid)}
                style={{
                  marginRight: '10px'
                }}
              />
            </div>
          );
        },
        meta: { cellProps: { className: 'text-900' } }
      }
    ],
    [
      t,
      selectAllChecked,
      handleSelectAll,
      handleTogglePermission,
      selectedPermissions
    ]
  );

  const posUserTableColumns: ColumnDef<SimplifiedPosRoleType>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: t('role_name'),
        cell: (rowData: any) => {
          return (
            <div style={{ paddingLeft: `${rowData?.row?.depth * 20}px` }}>
              {rowData?.row?.children?.length > 0 ? (
                <span style={{ fontWeight: 'bold' }}>{rowData?.row?.name}</span>
              ) : (
                <span>{rowData?.row?.name}</span>
              )}
            </div>
          );
        },
        meta: { cellProps: { className: 'text-900' } }
      },
      {
        accessorKey: 'check',
        header: () => (
          <div className="d-flex gap-2 align-items-center">
            <Form.Check
              type="checkbox"
              checked={selectAllChecked}
              onChange={handleSelectAll}
            />
            <p className="mb-0">{t('status')}</p>
          </div>
        ),
        cell: (rowData: any) => {
          const pid = rowData.row.pid;
          if (!pid) return null;
          const isChecked = selectedPermissions.some(
            perm => perm?.menu_pid === pid
          );
          return (
            <div>
              <Form.Check
                type="checkbox"
                checked={isChecked}
                onChange={() => handleTogglePermission(pid)}
                style={{ marginRight: '10px' }}
              />
            </div>
          );
        },
        meta: { cellProps: { className: 'text-900' } }
      }
    ],
    [
      t,
      selectAllChecked,
      handleSelectAll,
      handleTogglePermission,
      selectedPermissions
    ]
  );

  const usersBreadcrumbItems: PageBreadcrumbItem[] = useMemo(() => {
    return [
      {
        label: t('users'),
        url: '/clients'
      },
      {
        label: full_name || '',
        active: true
      }
    ];
  }, []);

  const table = useAdvanceTable({
    data: CreatedData,
    columns: userTableColumns,
    pageSize: 100,
    pagination: true,
    sortable: true,
    selection: false
  });

  const tablePos = useAdvanceTable({
    data: CreateDataPos,
    columns: posUserTableColumns,
    pageSize: 100,
    pagination: true,
    sortable: true,
    selection: false
  });

  const onSubmit = () => {
    if (user_id_number) {
      const menu_pids = selectedPermissions.map(perm => perm.menu_pid);

      userService?.update({ user_id_number, menu_pids }).then(res => {
        if (res) {
          refetch();
          refetchPermissions();

          dispatch(
            showAlert({
              title: `${t('successfully_added')}`,
              type: 'success'
            })
          );
        }
      });
    }
  };

  return (
    <div>
      <PageBreadcrumb items={usersBreadcrumbItems} />

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2>{full_name || ''}</h2>

        <div className="d-flex justify-content-between gap-2">
          <Button
            variant="primary"
            onClick={onSubmit}
            style={{ height: '48px' }}
          >
            {t('save')}
          </Button>
        </div>
      </div>

      <TabContainer activeKey={key} onSelect={(k: any) => setKey(k)}>
        <Nav variant="underline" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="WEB">WEB</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="POS">POS</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="WEB">
            <AdvanceTableProvider {...table}>
              <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
                {isLoading || isFetching ? (
                  <LoadingSpinner />
                ) : (
                  <AccordionAdvancedTable
                    tableProps={{ className: 'phoenix-table fs-9' }}
                    data={CreatedData}
                    columns={userTableColumns}
                  />
                )}
              </div>
            </AdvanceTableProvider>
          </Tab.Pane>
          <Tab.Pane eventKey="POS">
            <AdvanceTableProvider {...tablePos}>
              <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
                {isLoading || isFetching ? (
                  <LoadingSpinner />
                ) : (
                  <AccordionAdvancedTable
                    tableProps={{ className: 'phoenix-table fs-9' }}
                    data={CreateDataPos}
                    columns={posUserTableColumns}
                  />
                )}
              </div>
            </AdvanceTableProvider>
          </Tab.Pane>
        </Tab.Content>
      </TabContainer>
    </div>
  );
};

export default ClientsRoles;
